import { useTheme } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as config from "../../../config/config";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import { updateCurrentSalaryDialog } from "../../../redux/actions/dialog_action";
import { updateCurrentSalary } from "../../../redux/actions/user_action";
import {
  AddNowButton,
  ButtonsWrapper,
  CloseButtonWrapper,
  CurrentSalaryDialogStyled,
  CurrentSalaryImg,
  CurrentSalarySubTitle,
  DialogContentStyledWrapper,
  DialogTitleContainer,
  DialogTitleStyled,
  DividerStyled,
  DoNotShowAgainStyled,
  GrossSalaryLabel,
  GrossSalaryWrapper,
  InfoSectionWrapper,
  InfoText,
  MaybeLaterButton,
  RmText,
  SalaryInputDisplayRow,
  SalaryInputField,
  StyledAdornmentLabel,
} from "./styles";

export default function CurrentSalaryDialog({ open, onSubmit }) {
  const [salary, setSalary] = useState("");
  const rawSalaryRef = useRef("");
  const router = useRouter();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const userId = useSelector((state) => state.user?.user?.id);
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const dontShowKey = `currentSalaryDialogDontShow_${userId}`;
  const shownSessionKey = `currentSalaryDialogShownSession_${userId}`;

  const handleClose = () => {
    localStorage.setItem(shownSessionKey, "1");
    dispatch(updateCurrentSalaryDialog(false));
    router.reload();
  };

  const handleMaybeLater = () => {
    handleClose();
  };

  const handleDoNotShowAgain = () => {
    localStorage.setItem(dontShowKey, "1");
    dispatch(updateCurrentSalaryDialog(false));
    router.reload();
  };

  const handleAddNow = async () => {
    if (numericSalaryValue <= 0 || isNaN(numericSalaryValue)) {
      console.error("Invalid salary value for submission.");
      return;
    }
    try {
      await dispatch(
        updateCurrentSalary({ currentSalary: numericSalaryValue })
      );

      triggerSnackbarFunc({
        snackbarMessage: "Current salary added successfully.",
        severity: "success",
      });

      // Dialog disappears permanently
      localStorage.setItem(dontShowKey, "1");
      dispatch(updateCurrentSalaryDialog(false));
      router.reload();
    } catch (error) {
      triggerSnackbarFunc({
        snackbarMessage: "Failed to add current salary. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSalaryInputChange = (e) => {
    const rawValue = e.target.value;
    const numericOnly = rawValue.replace(/[^\d]/g, "");
    rawSalaryRef.current = numericOnly;
    setSalary(numericOnly);
  };

  const handleSalaryInputBlur = () => {
    if (rawSalaryRef.current) {
      const numericValue = Number(rawSalaryRef.current);
      if (!isNaN(numericValue) && numericValue >= 0) {
        const formatted = numericValue.toLocaleString("en-MY");
        setSalary(formatted);
      } else {
        setSalary("");
        rawSalaryRef.current = "";
      }
    } else {
      setSalary("");
      rawSalaryRef.current = "";
    }
  };

  const handleSalaryInputFocus = () => {
    setSalary(rawSalaryRef.current);
  };

  const numericSalaryValue = Number(rawSalaryRef.current);
  const isAddNowDisabled =
    rawSalaryRef.current.trim() === "" ||
    isNaN(numericSalaryValue) ||
    numericSalaryValue === 0;

  // Default desktop current salary GIF image
  let imageSrc = config.assetDomain + "/images/current-salary/Desktop.gif";
  if (isSmDown) {
    // Mobile current salary GIF image
    imageSrc = config.assetDomain + "/images/current-salary/Mobile.gif";
  } else if (isMdDown) {
    // Tablet current salary GIF image
    imageSrc = config.assetDomain + "/images/current-salary/Tablet.gif";
  }

  useEffect(() => {
    if (open) {
      setSalary("");
      rawSalaryRef.current = "";
    }
  }, [open]);

  return (
    <CurrentSalaryDialogStyled open={open} onClose={handleClose}>
      {/* Title Container */}
      <DialogTitleContainer>
        <DialogTitleStyled>
          <Typography>Let Us Know Your Current Salary</Typography>
        </DialogTitleStyled>
        {/* Close Icon */}
        <CloseButtonWrapper>
          <IconButton aria-label="close" onClick={handleClose} disableRipple>
            <CloseIcon />
          </IconButton>
        </CloseButtonWrapper>
      </DialogTitleContainer>

      {/* Content Container */}
      <DialogContentStyledWrapper>
        {/* Image Container */}
        <CurrentSalaryImg src={imageSrc} alt="current-salary-gif" />

        {/* Subtitle */}
        <InfoSectionWrapper>
          <CurrentSalarySubTitle variant="body1">
            💡 Want smarter job matches?
          </CurrentSalarySubTitle>
        </InfoSectionWrapper>

        {/* Text */}
        <InfoText variant="body2">
          Share with us your current salary to help us tailor job
          recommendations just for you. This info stays between us— we never
          share it with employers.
        </InfoText>
        <InfoText variant="body2">
          You can add / update this info anytime under <b>profile.</b>
        </InfoText>

        {/* Gross Monthly Salary Container */}
        <GrossSalaryWrapper>
          <GrossSalaryLabel variant="subtitle1">
            Gross Monthly Salary
          </GrossSalaryLabel>
          {/* Divider */}
          <DividerStyled />
          <SalaryInputDisplayRow>
            <StyledAdornmentLabel>MYR</StyledAdornmentLabel>
            <RmText variant="body1" component="span">
              RM
            </RmText>
            <SalaryInputField
              placeholder="00,000"
              value={salary}
              onChange={handleSalaryInputChange}
              onBlur={handleSalaryInputBlur}
              onFocus={handleSalaryInputFocus}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </SalaryInputDisplayRow>

          {/* Button */}
          <ButtonsWrapper>
            <MaybeLaterButton variant="outlined" onClick={handleMaybeLater}>
              Maybe Later
            </MaybeLaterButton>
            <AddNowButton
              variant="contained"
              onClick={handleAddNow}
              disabled={isAddNowDisabled}
            >
              Add Now
            </AddNowButton>
          </ButtonsWrapper>
        </GrossSalaryWrapper>

        <DoNotShowAgainStyled
          variant="subtitle1"
          onClick={handleDoNotShowAgain}
        >
          Don't show me this again
        </DoNotShowAgainStyled>
      </DialogContentStyledWrapper>
    </CurrentSalaryDialogStyled>
  );
}
